/**
 * @voltxampere/engine — Zener diode AC small-signal stamp (Sprint 23 B2)
 *
 * Linearised around the DC operating point (forward + breakdown
 * conductance toplam). Diode AC stamp pattern'ı genişletilmiştir:
 *
 *   g_d = g_F + g_R
 *   g_F = (Is/(n·Vt)) · exp(Vd/(n·Vt))                  [forward]
 *   g_R = (Ibv/(nz·Vt)) · exp(-(Vd + Vz)/(nz·Vt))       [breakdown]
 *
 * Junction-cap parametreleri (Cj0, Vj, M, Tt, FC) Sprint 22 D2'den
 * birebir miras — ama Zener için breakdown'da farklı cap davranışı
 * gerçekçi olmaz; smooth ekleme yeterli (cap sadece forward bias'da
 * dominant zaten — reverse'de depleted bölge dar, capacitance küçük).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { getNumericParam, getTerminalNetId } from "../terminal-utils.js";
import { diodeDepletionCap } from "./diode.js";
const MAX_EXP_ARG = 80;
const BOLTZMANN_K = 1.380649e-23;
const ELEMENTARY_CHARGE_Q = 1.602176634e-19;
function thermalVoltage(temperatureK) {
    return (BOLTZMANN_K * temperatureK) / ELEMENTARY_CHARGE_Q;
}
function readOptionalNumber(params, key, fallback, predicate) {
    const raw = params[key];
    if (typeof raw !== "number" || !Number.isFinite(raw))
        return fallback;
    return predicate(raw) ? raw : fallback;
}
export const zenerACStamp = (ctx, component) => {
    const Is = getNumericParam(component, "saturationCurrent", 1e-14);
    const N = getNumericParam(component, "ideality", 1);
    const Vz = getNumericParam(component, "breakdownVoltage", 5.1);
    const Ibv = getNumericParam(component, "breakdownCurrent", 5e-3);
    const Nz = getNumericParam(component, "breakdownIdeality", 1);
    if (!(Is > 0) || !(N > 0) || !(Vz > 0) || !(Ibv > 0) || !(Nz > 0)) {
        throw new StampParameterError(`zenerACStamp: component "${component.id}" has invalid Zener parameters (Is=${String(Is)}, N=${String(N)}, Vz=${String(Vz)}, Ibv=${String(Ibv)}, Nz=${String(Nz)})`, {
            meta: {
                stampType: "zener",
                parameter: "Is/N/Vz/Ibv/Nz",
                received: { Is, N, Vz, Ibv, Nz },
                expected: "all > 0 and finite",
                componentId: component.id,
            },
        });
    }
    const aNet = getTerminalNetId(component, "anode");
    const cNet = getTerminalNetId(component, "cathode");
    const a = ctx.nodeIndex(aNet);
    const c = ctx.nodeIndex(cNet);
    const Va = ctx.dcNodeVoltages.get(aNet) ?? 0;
    const Vc = ctx.dcNodeVoltages.get(cNet) ?? 0;
    const Vd = Va - Vc;
    const Vt = thermalVoltage(ctx.temperature);
    const nVt = N * Vt;
    const nzVt = Nz * Vt;
    const argF = Math.min(Vd / nVt, MAX_EXP_ARG);
    const gF = (Is * Math.exp(argF)) / nVt;
    const argR = Math.min(-(Vd + Vz) / nzVt, MAX_EXP_ARG);
    const expR = argR > -MAX_EXP_ARG ? Math.exp(argR) : 0;
    const gR = (Ibv * expR) / nzVt;
    const gd = gF + gR;
    // Junction caps (zero-default per Sprint 22 D2).
    const caps = {
        Cj0: readOptionalNumber(component.params, "Cj0", 0, (v) => v >= 0),
        Vj: readOptionalNumber(component.params, "Vj", 1, (v) => v > 0),
        M: readOptionalNumber(component.params, "M", 0.5, (v) => v > 0),
        Tt: readOptionalNumber(component.params, "Tt", 0, (v) => v >= 0),
        FC: readOptionalNumber(component.params, "FC", 0.5, (v) => v >= 0 && v < 1),
    };
    const Cdep = diodeDepletionCap(Vd, caps);
    const Cdiff = gd * caps.Tt;
    const Ctot = Cdep + Cdiff;
    const susceptance = ctx.omega * Ctot;
    ctx.addY(a, a, gd, susceptance);
    ctx.addY(c, c, gd, susceptance);
    ctx.addY(a, c, -gd, -susceptance);
    ctx.addY(c, a, -gd, -susceptance);
};
//# sourceMappingURL=zener.js.map